import { creatorApiClient } from './apiClient';

export interface EscalateQuestionRequest {
  aiLogId: string;
  segmentId: string;
  question: string;
  sentenceText: string;
  translation?: string;
  showLearnerName?: boolean;
}

export interface EscalateQuestionResponse {
  id: string;
  status: 'pending';
  createdAt: string;
}

export interface SentenceFaqQuestion {
  text: string;
  learnerDisplayName: string | null;
  createdAt: string;
}

export interface SentenceFaq {
  id: string;
  answerText: string;
  creatorDisplayName: string;
  createdAt: string;
  questions: SentenceFaqQuestion[];
}

export interface CreatorQuestion {
  id: string;
  courseId: string;
  courseTitle: string;
  segmentId: string;
  sentenceText: string;
  translation: string | null;
  learnerId: string;
  learnerDisplayName: string | null;
  showLearnerName: boolean;
  originalQuestion: string;
  aiLogId: string | null;
  aiAnswer: string | null;
  status: 'pending' | 'answered' | 'dismissed';
  answerId: string | null;
  answerText: string | null;
  dismissReason: string | null;
  createdAt: string;
  answeredAt: string | null;
  dismissedAt: string | null;
}

export interface CreatorQuestionsBySegment {
  segmentId: string;
  sentenceText: string;
  translation: string | null;
  questions: CreatorQuestion[];
}

export interface CreatorQuestionsByCourse {
  courseId: string;
  courseTitle: string;
  pendingCount: number;
  segments: CreatorQuestionsBySegment[];
}

export interface CreatorQaStats {
  pendingCount: number;
  answeredCount: number;
  dismissedCount: number;
  avgResponseTimeMinutes: number | null;
  dismissRate: number;
}

export const creatorQuestionsService = {
  async escalate(payload: EscalateQuestionRequest): Promise<EscalateQuestionResponse> {
    const response = await creatorApiClient.post<EscalateQuestionResponse>(
      '/creator-questions/escalate',
      payload,
    );
    return response.data;
  },

  async getSentenceFaqs(courseId: string, segmentId: string): Promise<SentenceFaq[]> {
    const response = await creatorApiClient.get<SentenceFaq[]>('/creator-questions/sentence-faqs', {
      params: { courseId, segmentId },
    });
    return response.data;
  },

  async listForCreator(filter: {
    courseId?: string;
    status?: 'pending' | 'answered' | 'dismissed';
  }): Promise<CreatorQuestionsByCourse[]> {
    const response = await creatorApiClient.get<CreatorQuestionsByCourse[]>('/creator/questions', {
      params: filter,
    });
    return response.data;
  },

  async stats(): Promise<CreatorQaStats> {
    const response = await creatorApiClient.get<CreatorQaStats>('/creator/questions/stats');
    return response.data;
  },

  async answer(
    questionId: string,
    payload: { answerText: string; applyToQuestionIds?: string[]; isPublished?: boolean },
  ): Promise<CreatorQuestion> {
    const response = await creatorApiClient.post<CreatorQuestion>(
      `/creator/questions/${questionId}/answer`,
      payload,
    );
    return response.data;
  },

  async dismiss(questionId: string, payload: { reason?: string }): Promise<CreatorQuestion> {
    const response = await creatorApiClient.post<CreatorQuestion>(
      `/creator/questions/${questionId}/dismiss`,
      payload,
    );
    return response.data;
  },
};

export default creatorQuestionsService;
